import React, { Component, useState, useEffect, useMemo, useRef } from 'react'
import { Grid } from '@mui/material'
import { JsonFormsDispatch } from '@jsonforms/react';
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import {
  materialRenderers,
  materialCells,
} from '@jsonforms/material-renderers';
import TextField from "@mui/material/TextField";
import { JsonForms } from '@jsonforms/react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import FileWidget from './FileWidget'
import { useNavigate } from 'react-router-dom'
import { setPrevStage, clearJobData, setJobData, clearJobDetail } from '../../../store/job/jobSlice'
import { useDispatch, useSelector } from 'react-redux'
import JobItemsTableRenderer from './JobItemsTableRenderer';
import { rankWith, isControl, uiTypeIs, optionIs, and, scopeEndsWith } from '@jsonforms/core';
import { withJsonFormsLayoutProps, withJsonFormsControlProps } from '@jsonforms/react';
import { setCompany, setProcessTab, get_company_process, setStageTab } from '../../../store/user/userSlice';
import { isAuth } from '../../../helpers/cookies';
import { formatDateTime } from '../../../helpers/cookies';
import { getStageDisplayName } from '../../../helpers/stageDisplay';
import { grey } from "@mui/material/colors";
import AddToPhotosOutlinedIcon from '@mui/icons-material/AddToPhotosOutlined';
import IconButton from '@mui/material/IconButton';
import { SvgIcon } from "@mui/material";
import SendProofModal from './SendProofModal';
import SendIcon from '@mui/icons-material/Send';
import Tooltip from "@mui/material/Tooltip";
import CollectionsBookmarkOutlinedIcon from '@mui/icons-material/CollectionsBookmarkOutlined';
import BrandLibraryModal from './BrandLibraryModal';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import PdfThumbnail from './PdfThumbnail';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Chip } from "@mui/material";

const EditSvgIcon = (props) => (
  <SvgIcon {...props}>
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </g>
  </SvgIcon>
);

const theme = createTheme({
  components: {
    MuiInputLabel: {
      defaultProps: {
        shrink: true
      },
      styleOverrides: {
        root: {
          fontSize: '0.9rem',
          backgroundColor: 'white', // Matches the container background
          padding: '0 4px',
        }
      }
    },
    MuiInputBase: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            color: "#000000",
            WebkitTextFillColor: "#000000 !important",
            opacity: 1
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: "#BDBDBD"
          }
        },
        input: {
          "&.Mui-disabled": {
            color: "#000000 !important",
            WebkitTextFillColor: "#000000 !important",
            opacity: 1
          }
        }
      }
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: '17px', // Uniform spacing
          width: '100%'
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: '20px'
        }
      }
    }

  }
});


/* ===== CUSTOM RENDERERS MOVED OUTSIDE FOR STABILITY ===== */

const SubmittedDateLabelRendererFn = ({ uischema, data }) => {
  const path = uischema.scope ? uischema.scope.split('/').pop() : 'submitted_date';
  const label = uischema.text || uischema.label || "Submitted Date";
  const value = data?.[path];

  return (
    <TextField
      label={label}
      value={value ? formatDateTime(value) : ''}
      fullWidth
      variant="outlined"
      size="small"
      InputProps={{
        readOnly: true,
        sx: { color: grey[700] },
        label: label
      }}
      sx={{ mb: 2.5 }}
    />
  );
};
const SubmittedDateLabelRenderer = withJsonFormsLayoutProps(SubmittedDateLabelRendererFn);

const CompletedDateLabelRendererFn = ({ uischema, data }) => {
  const path = uischema.scope.replace('#/properties/', '');
  const value = data?.[path] ? formatDateTime(data[path]) : '';
  const label = uischema.text || uischema.label || "Completed Date";
  return (
    <TextField
      label={label}
      value={value}
      fullWidth
      variant="outlined"
      size="small"
      InputProps={{
        readOnly: true,
        sx: { color: grey[700] },
        label: label
      }}
      sx={{ mb: 2.5 }}
    />
  );
};
const CompletedDateLabelRenderer = withJsonFormsLayoutProps(CompletedDateLabelRendererFn);

const BorderedBooleanRendererFn = ({ data, uischema }) => {
  const { label } = uischema;
  const value = data || false;
  return (
    <Box sx={{ border: '1px solid rgba(0, 0, 0, 0.23)', borderRadius: '4px', padding: '8.5px 14px', display: 'flex', alignItems: 'center', mb: 2.5, bgcolor: 'white' }}>
      <input type="checkbox" checked={value} disabled style={{ width: '18px', height: '18px', marginRight: '10px', accentColor: '#ff6100', cursor: 'default' }} />
      <Typography sx={{ flexGrow: 1, color: 'text.secondary', fontSize: '1rem' }}>{label}</Typography>
    </Box>
  );
};
const BorderedBooleanRenderer = withJsonFormsControlProps(BorderedBooleanRendererFn);
const borderedBooleanTester = rankWith(3, and(uiTypeIs('Control'), optionIs('renderer', 'BorderedBoolean')));

const DescriptionRendererFn = ({ data, uischema }) => {
  const value = data || "";
  const baseLabel = uischema.label || "Description";
  const label = baseLabel.includes('*') ? baseLabel : `${baseLabel} *`;
  return (
    <TextField
      label={label}
      placeholder={baseLabel}
      value={value}
      multiline
      fullWidth
      variant="outlined"
      size="small"
      InputProps={{ readOnly: true, sx: { color: grey[700] }, label }}
      sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { minHeight: '107px', alignItems: 'flex-start', backgroundColor: 'white' } }}
    />
  );
};
const DescriptionRenderer = withJsonFormsControlProps(DescriptionRendererFn);
const logoNameTester = rankWith(10, (uischema) =>
  uischema.scope === '#/properties/logo_name'
);
const LogoNameRendererFn = ({ data, label, enabled }) => {
  const cleanLabel = (label || "").replace(/\s?\*$/, "").replace(/\s?\*$/, "");
  return (
    <TextField
      label={cleanLabel}
      value={data || ""}
      required={false}
      disabled={true}
      fullWidth
      variant="outlined"
      size="small"
      sx={{ 
        mb: 2.5,
        "& .MuiInputBase-root.Mui-disabled": {
          bgcolor: "white", 
        }
      }}
    />
  );
};
const LogoNameRenderer = withJsonFormsControlProps(LogoNameRendererFn);

const virtualRequestTester = rankWith(10, (uischema) =>
  uischema.scope === '#/properties/virtual_request'
);
const VirtualRequestRendererFn = ({ data, label }) => {
  const cleanLabel = (label || "").replace(/\s?\*$/, "").replace(/\s?\*$/, "");
  return (
    <TextField
      label={cleanLabel}
      value={data || ""}
      required={false}
      disabled={true}
      fullWidth
      variant="outlined"
      size="small"
      sx={{ 
        mb: 2.5,
        "& .MuiInputBase-root.Mui-disabled": {
          bgcolor: "white", 
        }
      }}
    />
  );
};
const VirtualRequestRenderer = withJsonFormsControlProps(VirtualRequestRendererFn);

const CustomHorizontalLayoutRenderer = withJsonFormsLayoutProps((props) => {
  const { uischema, renderers, cells, schema, path, enabled, visible } = props;
  if (visible === false) return null;
  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2.5}>
        {uischema.elements.map((element, index) => (
          <Grid item xs={12} sm={element.options?.xs || (12 / uischema.elements.length)} key={index}>
            <JsonFormsDispatch schema={schema} uischema={element} path={path} enabled={enabled} renderers={renderers} cells={cells} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
});

const CustomVerticalLayoutRenderer = withJsonFormsLayoutProps((props) => {
  const { uischema, renderers, cells, schema, path, enabled, visible } = props;
  if (visible === false) return null;
  return (
    <Box sx={{ width: '100%' }}>
      <Grid container direction="column" spacing={0}>
        {uischema.elements.map((element, index) => (
          <Grid item xs={12} key={index}>
            <JsonFormsDispatch schema={schema} uischema={element} path={path} enabled={enabled} renderers={renderers} cells={cells} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
});
const JobDetail = ({
  mode,
  job_form,
  job_data,
  job_id,
  job_detail,
  proof_log_data,
  handleZipDownload,
  handleSendProof
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { company_process, company_process_stage, selected_process_tab } = useSelector(state => state.user);
  const { sidebar_menus } = useSelector(state => state.job);

  const [formData, setFormData] = useState(null);
  const [openSendProof, setOpenSendProof] = useState(false);
  const [openBrandLibrary, setOpenBrandLibrary] = useState(false);

  useEffect(() => {
    if (mode !== 'create' && job_detail) {
      const jd = job_detail.job_data || {};

      const sendArt =
        jd.send_art_to_customer === true ||
        jd.send_art_to_customer === 'true' ||
        jd.send_art_to_customer === 1 ||
        jd.sendarttocustomer === true ||
        jd.sendarttocustomer === 'true' ||
        jd.sendarttocustomer === 1;

      setFormData({
        ...jd,
        send_art_to_customer: sendArt,
        customer_email: jd.customer_email || jd.customeremail || "",
        creator_email: job_detail.creator_email || "",
        stage: getStageDisplayName(job_detail.stage?.name || jd.stage),
        status: getStageDisplayName(job_detail.stage?.name || jd.stage || jd.status),
        created_date: formatDateTime(job_detail.created_date),
        submitted_date: jd.submitted_date || job_detail.created_date,
        reps_email: jd.reps_email || jd.rep_email || jd.email || "",
        rep_number: jd.rep_number || "",
        completed_file_date:
          jd?.completed_file_date ||
          jd?.job_items?.[0]?.completed_file_date ||
          null
      });

      dispatch(setPrevStage(job_detail.stage?.name));
      dispatch(setStageTab(job_detail.stage?.id));
    } else {
      setFormData(job_data || {});
    }
  }, [mode, job_detail, job_data, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearJobDetail());
    };
  }, [dispatch]);

  const fileInputTester = rankWith(
    5,
    and(uiTypeIs('Control'), optionIs('renderer', 'fileInputTester'))
  );

  const fileInputWithBrandTester = rankWith(
    5,
    and(uiTypeIs('Control'), optionIs('renderer', 'FileInputWithBrandOption'))
  );

  const contextRef = useRef({ mode, proof_log_data, job_id, job_detail, handleZipDownload });
  useEffect(() => {
    contextRef.current = { mode, proof_log_data, job_id, job_detail, handleZipDownload };
  }, [mode, proof_log_data, job_id, job_detail, handleZipDownload]);

  const ButtonRendererFn = useMemo(() => {
    return ({ uischema }) => {
      const { label, options } = uischema;
      const { job_detail: jd, handleZipDownload: hDownload, job_id: jId } = contextRef.current;
      if (options?.onClickAction === 'downloadZip') {
        if (!jd?.comp_file_list || jd.comp_file_list.length <= 1) {
          return null;
        }
      }
      return (
        <Button
          sx={{ background: '#ff6100', textTransform: 'none' }}
          onClick={() => hDownload && hDownload(jId, jd.comp_file_list)}
        >
          {label}
        </Button>
      );
    }
  }, [contextRef]);
  const ButtonRenderer = useMemo(() => withJsonFormsLayoutProps(ButtonRendererFn), [ButtonRendererFn, withJsonFormsLayoutProps]);

  // Rank higher than standard control for description scope
  const descriptionTester = rankWith(4, (uischema) => {
    return uischema.scope === '#/properties/description';
  });

  const fileNameTester = rankWith(5, (uischema) => {
    if (!uischema.scope) return false;
    const scope = uischema.scope;

    // Check for explicit renderer option used in JobItemsTableRenderer
    if (uischema.options?.renderer === 'FileInputField') return true;

    return (
      scope.endsWith('submitted_file_list') ||
      scope.endsWith('completed_file_list') ||
      scope.endsWith('submitted_file') ||
      scope.endsWith('completed_file') ||
      scope.endsWith('eps_file') ||
      scope.endsWith('logo_file')
    );
  });

  const FileWidgetRenderer = useMemo(() => {
    return props => {
      const { mode, proof_log_data, job_id } = contextRef.current;
      return (
        <FileWidget
          {...props}
          mode={mode}
          proof_log_data={proof_log_data}
          showBrandOption={false}
          showLibraryLink={true}
          job_id={job_id}
        />
      );
    };
  }, [contextRef]);

  const FileWidgetWithBrandRenderer = useMemo(() => {
    return props => {
      const { mode, proof_log_data, job_id } = contextRef.current;
      return (
        <FileWidget
          {...props}
          mode={mode}
          proof_log_data={proof_log_data}
          showBrandOption={true}
          showLibraryLink={false}
          job_id={job_id}
        />
      );
    };
  }, [contextRef]);

  const FileNameWidgetRenderer = useMemo(() => {
    return props => {
      const { mode, proof_log_data, job_id } = contextRef.current;
      const isCompleted = props.uischema.scope && props.uischema.scope.includes('completed');
      return (
        <FileWidget
          {...props}
          mode={mode}
          proof_log_data={proof_log_data}
          showBrandOption={isCompleted}
          showLibraryLink={false}
          job_id={job_id}
          compact={props.path?.includes('job_items')}
          enabled={false}
        />
      );
    };
  }, [contextRef]);

  const renderers = useMemo(() => [
    { tester: rankWith(3, uiTypeIs('HorizontalLayout')), renderer: CustomHorizontalLayoutRenderer },
    { tester: rankWith(3, uiTypeIs('VerticalLayout')), renderer: CustomVerticalLayoutRenderer },
    {
      tester: rankWith(4, and(uiTypeIs('Control'), scopeEndsWith('job_items'))),
      renderer: JobItemsTableRenderer
    },
    ...materialRenderers,
    { tester: fileInputTester, renderer: FileWidgetRenderer },
    { tester: fileInputWithBrandTester, renderer: FileWidgetWithBrandRenderer },
    { tester: rankWith(3, uiTypeIs('Button')), renderer: ButtonRenderer },
    {
      tester: rankWith(5, (uischema) =>
        uischema.type === 'Submitted Date Label' || uischema.scope === '#/properties/submitted_date'
      ),
      renderer: SubmittedDateLabelRenderer
    },
    {
      tester: rankWith(5, (uischema) =>
        uischema.type === 'Completed Date Label' || uischema.scope === '#/properties/completed_date' || uischema.scope === '#/properties/completed_file_date'
      ),
      renderer: CompletedDateLabelRenderer
    },
    { tester: borderedBooleanTester, renderer: BorderedBooleanRenderer },
    { tester: descriptionTester, renderer: DescriptionRenderer },
    { tester: logoNameTester, renderer: LogoNameRenderer },
    { tester: virtualRequestTester, renderer: VirtualRequestRenderer },
    { tester: fileNameTester, renderer: FileNameWidgetRenderer }
  ], [
    CustomHorizontalLayoutRenderer, CustomVerticalLayoutRenderer, JobItemsTableRenderer, ButtonRenderer,
    SubmittedDateLabelRenderer, CompletedDateLabelRenderer, borderedBooleanTester,
    descriptionTester, FileWidgetRenderer, FileWidgetWithBrandRenderer, FileNameWidgetRenderer,
    fileInputTester, fileInputWithBrandTester, fileNameTester, logoNameTester, virtualRequestTester
  ]);

  const dynamicSchema = useMemo(() => {
    if (!job_form?.job_schema) return null;
    const schemaCopy = JSON.parse(JSON.stringify(job_form.job_schema));
    if (!schemaCopy.properties) schemaCopy.properties = {};

    const ensureProperty = (propName, type = 'string', title = undefined) => {
      if (!schemaCopy.properties[propName]) {
        schemaCopy.properties[propName] = { type, title };
      }
    };

    ensureProperty('send_art_to_customer', 'boolean', 'Send Art to Customer');
    ensureProperty('customer_email', 'string', 'Customer Email');
    ensureProperty('job_items', 'array', 'Job Items');
    ensureProperty('stage', 'string', 'Stage');
    ensureProperty('status', 'string', 'Status');
    
    // Remove enum if exists so it renders as a regular TextField instead of a Select
    if (schemaCopy.properties.stage) delete schemaCopy.properties.stage.enum;
    if (schemaCopy.properties.status) delete schemaCopy.properties.status.enum;

    ensureProperty('submitted_date', 'string', 'Submitted Date');
    ensureProperty('reps_email', 'string', 'Reps Email');
    ensureProperty('rep_number', 'string', 'Rep Number');

    if (schemaCopy.properties.job_items && !schemaCopy.properties.job_items.items) {
      schemaCopy.properties.job_items.items = { type: 'object', properties: {} };
    }

    return schemaCopy;
  }, [job_form, company_process_stage]);

  const dynamicUiSchema = useMemo(() => {
    if (!job_form?.job_ui_update_schema) return null;
    const uiSchemaCopy = JSON.parse(JSON.stringify(job_form.job_ui_update_schema));

    const filterFields = (elements) => {
      if (!elements) return;
      for (let i = 0; i < elements.length; i++) {
        const el = elements[i];
        if (el.scope === '#/properties/proof_request_type') {
          elements.splice(i, 1);
          i--;
          continue;
        }
        if (el.elements) filterFields(el.elements);
        if (el.options?.detail?.elements) filterFields(el.options.detail.elements);
      }
    };

    filterFields(uiSchemaCopy.elements);

    const hasControl = (elements, scope) => {
      if (!elements) return false;
      for (const el of elements) {
        if (el.scope === scope) return true;
        if (el.elements && hasControl(el.elements, scope)) return true;
        if (el.options?.detail?.elements && hasControl(el.options.detail.elements, scope)) return true;
      }
      return false;
    };

    const injectControl = (elements, scope, label, rule = null, options = null) => {
      let targetElements = elements;
      let lastIndex = -1;

      const findLocation = (elems) => {
        if (!elems) return false;
        for (let i = 0; i < elems.length; i++) {
          if (elems[i].scope === '#/properties/instructions') {
            targetElements = elems;
            lastIndex = i;
            return true;
          }
          if (elems[i].elements && findLocation(elems[i].elements)) return true;
          if (elems[i].options?.detail?.elements && findLocation(elems[i].options.detail.elements)) return true;
        }
        return false;
      };

      findLocation(elements);

      const newEl = { type: 'Control', scope, label };
      if (rule) newEl.rule = rule;
      if (options) newEl.options = options;

      if (lastIndex !== -1) {
        targetElements.splice(lastIndex + 1, 0, newEl);
      } else {
        elements.push(newEl);
      }
    };

    if (!hasControl(uiSchemaCopy.elements, '#/properties/send_art_to_customer')) {
      injectControl(uiSchemaCopy.elements, '#/properties/send_art_to_customer', 'Send Art to Customer', null, { renderer: 'BorderedBoolean' });
    }

    if (!hasControl(uiSchemaCopy.elements, '#/properties/customer_email')) {
      // In Detail view, we can show it without the rule or with a simpler logic 
      // since the data is already fixed. Let's just inject it.
      injectControl(uiSchemaCopy.elements, '#/properties/customer_email', 'Customer Email');
    }

    if (!hasControl(uiSchemaCopy.elements, '#/properties/rep_number')) {
      uiSchemaCopy.elements.unshift({
        type: 'Control',
        scope: '#/properties/rep_number',
        label: 'Rep Number'
      });
    }

    if (!hasControl(uiSchemaCopy.elements, '#/properties/reps_email')) {
      uiSchemaCopy.elements.unshift({
        type: 'Control',
        scope: '#/properties/reps_email',
        label: 'Reps Email'
      });
    }

    const forceStatusLabel = (elements) => {
      if (!elements) return;
      for (const el of elements) {
        if (el.scope === '#/properties/stage' || el.scope === '#/properties/status') {
          el.label = 'Status';
        }
        if (el.elements) forceStatusLabel(el.elements);
        if (el.options?.detail?.elements) forceStatusLabel(el.options.detail.elements);
      }
    };

    forceStatusLabel(uiSchemaCopy.elements);

    if (!hasControl(uiSchemaCopy.elements, '#/properties/stage') && !hasControl(uiSchemaCopy.elements, '#/properties/status')) {
      const stageControl = {
        type: 'Control',
        scope: '#/properties/stage',
        label: 'Status'
      };
      if (uiSchemaCopy.type === "Categorization" && uiSchemaCopy.elements?.[0]?.elements) {
        uiSchemaCopy.elements[0].elements.unshift(stageControl);
      } else if (uiSchemaCopy.elements) {
        uiSchemaCopy.elements.unshift(stageControl);
      }
    }

    return uiSchemaCopy;
  }, [job_form]);

  const statusList = [
    "New",
    "In Progress",
    "Completed",
    "Hold",
    "Approval Requested",
    "Approved",
    "Rejected",
    "Quote Requested",
    // "Quote Approval Needed",
    "Archived",
  ];

  return (
    <Box
      className="jobWrapper"
      sx={{
        flex: 1,
        mr: 1,

      }}
    >
      <Box
        sx={{
          bgcolor: "#fff",
          px: 2,
          mt: 0,
          py: 1.5, // Standardized padding
          minHeight: '45px',
          borderTopLeftRadius: '10px',
          borderTopRightRadius: '10px',
          borderBottom: '20px solid #FFF5F2',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        {/* LEFT SIDE: Title */}
        <Typography
          sx={{
            fontSize: "15px",
            fontWeight: 600,
            color: 'black',
            cursor: 'pointer'
          }}
          onClick={() => navigate('/')}
        >
          Job Details / {job_id}
        </Typography>

        {/* RIGHT SIDE: Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {job_detail?.stage?.name?.toLowerCase().trim() === 'completed' && (
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#ff6100',
                color: '#fff',
                textTransform: 'none',
                borderRadius: '5px',
                fontSize: '14px',
                px: 3,
                py: 0.75,
                '&:hover': {
                  backgroundColor: '#e55600'
                }
              }}
              onClick={() => handleSendProof && handleSendProof(job_id)}
            >
              {formData?.send_art_to_customer ? 'Send Art to Customer' : 'Send Art to Rep'}
            </Button>
          )}

          <Button
            variant="contained"
            sx={{
              backgroundColor: '#ff6100',
              color: '#fff',
              textTransform: 'none',
              borderRadius: '5px',
              fontSize: '14px',
              px: 3,
              py: 0.75,
              '&:hover': {
                backgroundColor: '#e55600'
              }
            }}
            onClick={() => {
              const url = selected_process_tab ? `/update-job/${job_id}?process_id=${selected_process_tab}` : `/update-job/${job_id}`;
              navigate(url);
            }}
          >
            Edit
          </Button>
        </Box>
      </Box>

      {/* REMOVED SECONDARY HEADER BLOCK THAT WAS HERE */}

      <Box sx={{
        px: 2,
        py: 2,
        bgcolor: 'white',
        borderTopLeftRadius: '10px',
        borderTopRightRadius: '10px',
        width: '100%',
        maxWidth: '100%'
      }}>
        <ThemeProvider theme={theme}>
          <Box
            sx={{
              width: '100%'
            }}
          >

            {formData && dynamicSchema && dynamicUiSchema && (
              <JsonForms
                schema={dynamicSchema}
                uischema={dynamicUiSchema}
                data={formData}
                renderers={renderers}
                cells={materialCells}
                readonly
                validationMode="NoValidation"
              />
            )}
            {formData?.logo_references?.length > 0 && (
              <Box sx={{ mt: 3, p: 2, bgcolor: '#fff', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography sx={{ fontWeight: 600, mb: 2 }}>Logo References</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {formData.logo_references.map((logo, index) => {
                    const path = logo.file_path || logo;
                    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
                    const url = path.startsWith('http')
                      ? path
                      : `https://sewrobj.us-east-1.linodeobjects.com/${cleanPath}`;
                    const isImage = /\.(jpg|jpeg|png|gif|webp)($|\?)/i.test(url);
                    const isPdf = /\.pdf($|\?)/i.test(url);

                    return (
                      <Box
                        key={index}
                        sx={{
                          width: 100,
                          height: 100,
                          border: '1px solid #eee',
                          borderRadius: 1,
                          bgcolor: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        {isImage ? (
                          <img
                            src={url}
                            alt="logo"
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            onClick={() => window.open(url, '_blank')}
                          />
                        ) : isPdf ? (
                          <PdfThumbnail
                            url={url}
                            width={100}
                            onClick={() => {
                              // Create a temporary link to download the PDF
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = path.split('/').pop() || 'document.pdf';
                              link.target = '_blank';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                          />
                        ) : (
                          <Typography
                            variant="caption"
                            onClick={() => window.open(url, '_blank')}
                          >
                            FILE
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/*               <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 5 }}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    dispatch(clearJobData());
                    navigate('/');
                  }}
                  sx={{ mr: 2 }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  sx={{ bgcolor: '#ff6100' }}
                  onClick={() => {
                    const url = selected_process_tab ? `/update-job/${job_id}?process_id=${selected_process_tab}` : `/update-job/${job_id}`;
                    navigate(url);
                  }}
                >
                  Update
                </Button>
              </Box> */}

          </Box>
        </ThemeProvider>
      </Box>



      <BrandLibraryModal
        open={openBrandLibrary}
        onClose={() => setOpenBrandLibrary(false)}
        onSelect={(selected) => {
          setOpenBrandLibrary(false);
        }}
      />
    </Box>
  );
};

export default JobDetail;